import { Injectable } from '@angular/core';
import { map, Observable, of, Subject, catchError, throwError } from 'rxjs';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    jwtHelper: JwtHelperService = new JwtHelperService();
    private URL = environment.services.SisDenuncia;
    private token: string | null = null;
    private readonly TOKEN_KEY = 'AUTH_TOKEN';
    
    // Flag para evitar múltiples renovaciones simultáneas
    private isRefreshing = false;
    private refreshTokenSubject = new Subject<string>();

    constructor(
        private oStorageService: StorageService,
        private oHttpClient: HttpClient,
        private router: Router
    ) {
        // Intentar recuperar token del storage al inicializar
        this.token = this.oStorageService.getItem(this.TOKEN_KEY);
    }

    logout() {
        localStorage.clear();
        this.oStorageService.removeAll();
        this.clearToken();
    }

    isAuthenticated(): boolean {
        //Obtener el token de memoria, Verificar si el token existe y no ha expirado
        const token = this.getToken();
        if (token === null)
            return false;
        return this.jwtHelper.isTokenExpired(token) === false && this.isAuthenticatedInValue();
    }

    isAuthenticatedInValue(): boolean {
        // Implementa lógica adicional si es necesario para verificar otras condiciones de autenticación
        // por ejemplo, verifica roles, permisos, etc.
        const token = this.getToken();
        if (token === null) {
            return false;
        }
        const decodedToken = this.jwtHelper.decodeToken(token);
        if (decodedToken === null) {
            return false;
        }
        return true;
    }

    fetchToken(subject: string): Observable<string> {
        const request = {
            subject: subject,
        };

        return this.oHttpClient.post<any>(`${this.URL}Token/generar-token`, request).pipe(
            map((response) => {
                if (response.bSuccess && response.oData) {
                    const tokenJWT = response.oData;
                    this.setToken(tokenJWT);
                    return tokenJWT;
                }
                const message = response?.sMessage || 'No se pudo generar el token';
                console.error('No se pudo generar el token:', message);
                throw new Error(message);
            }),
            catchError((error) => {
                console.error('Error al generar el token:', error);
                return throwError(() => error);
            })
        );
    }

    getToken(): string | null {
        // Si no está en memoria, intentar recuperar del storage
        if (!this.token) {
            this.token = this.oStorageService.getItem(this.TOKEN_KEY);
        }
        return this.token;
    }

    setToken(token: string): void {
        this.token = token;
        // Persistir token en el storage encriptado
        this.oStorageService.setItem(this.TOKEN_KEY, token);
    }

    clearToken(): void {
        this.token = null;
        // Remover token del storage
        this.oStorageService.removeItem(this.TOKEN_KEY);
    }

    /**
     * Verifica si el token está EXPIRADO (no 5 minutos antes).
     * Retorna true solo si el token ya pasó su fecha de expiración.
     */
    isTokenExpired(): boolean {
        const token = this.getToken();
        if (!token) {
            return true;
        }
        return this.jwtHelper.isTokenExpired(token) === true;
    }

    /**
     * Obtiene un nuevo token si el actual está expirado.
     * Evita múltiples renovaciones simultáneas.
     */
    ensureValidToken(subject: string): Observable<string> {
        const token = this.getToken();

        // Si no existe token o está expirado, generarlo o renovarlo según corresponda
        if (!token || this.isTokenExpired()) {
            // Si ya hay una renovación/generación en progreso, esperar a que termine
            if (this.isRefreshing) {
                return new Observable((observer) => {
                    const subscription = this.refreshTokenSubject.subscribe({
                        next: (newToken: string) => {
                            observer.next(newToken);
                            observer.complete();
                            subscription.unsubscribe();
                        },
                        error: (error) => {
                            observer.error(error);
                            subscription.unsubscribe();
                        }
                    });
                });
            }

            this.isRefreshing = true;

            const tokenRequest$ = !token
                ? this.fetchToken(subject)
                : this.renewToken(subject);

            return tokenRequest$.pipe(
                map((newToken: string) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(newToken);
                    return newToken;
                }),
                catchError((error) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.error(error);
                    return throwError(() => error);
                })
            );
        }

        // Token es válido, retornarlo inmediatamente
        return of(token);
    }


    goToLogin(): void {
        this.logout();
        this.router.navigate(['/']); // O la ruta de login que corresponda
    }

    renewToken(subject: string): Observable<string> {
        const headers = new HttpHeaders().set('X-Api-Key', 'my-api-key');

        const renovarRequest = {
            subject
        };

        return this.oHttpClient.post<any>(`${this.URL}Token/renovar-token`, renovarRequest, { headers }).pipe(
            map(response => {
                if (response.bSuccess && response.oData) {
                    const newToken = response.oData;
                    this.setToken(newToken);
                    return newToken;
                } else {
                    console.error('No se pudo renovar el token:', response.sMessage);
                    throw new Error(response.sMessage || 'No se pudo renovar el token');
                }
            }),
            catchError((error) => {
                this.isRefreshing = false;
                console.error('Error al renovar token:', error);
                return throwError(() => error);
            })
        );
    }
}
